import AppSettings from "../AppSettings";
import { InitializeUnauthInstance, InitializeAuthInstance } from "../axiosInterceptor";
import { v4 as uuidv4 } from 'uuid';
import { AuthRefreshFailedError } from "../Errors/AuthRefreshFailedError";
import { EmptyApiResponse } from "@layerswap/widget-types";
import { ErrorHandler } from "../../lib/ErrorHandler";
import { startApiOperation } from '../widgetTelemetry';
import { resolveAtomicDepositActions } from '../atomicBatchActions';
const IGNORED_API_ERROR_CODES = [
    'ROUTE_NOT_FOUND_ERROR',
    'PRICE_IMPACT_TOO_HIGH',
    'GREATER_THAN_MAX_ERROR',
    'LESS_THAN_MIN_ERROR'
];
class LayerSwapApiClient {
    constructor() {
        this.fetcher = (url, options) => this.AuthenticatedRequest("GET", url, undefined, undefined, options?.signal);
        this._authInterceptor = InitializeAuthInstance();
        this._unauthInterceptor = InitializeUnauthInstance(LayerSwapApiClient.apiBaseEndpoint);
    }
    async GetRoutesAsync(direction, _useFrontendSwap = true) {
        return await this.UnauthenticatedRequest("GET", `/${direction}?include_unmatched=true&include_swaps=true&include_unavailable=true&use_frontend_swap=true`);
    }
    async GetSourceExchangesAsync() {
        return await this.UnauthenticatedRequest("GET", `/source_exchanges`);
    }
    async GetLSNetworksAsync() {
        return await this.UnauthenticatedRequest("GET", `/networks`);
    }
    async CreateSwapAsync(params) {
        const correlationId = uuidv4();
        const response = await this.AuthenticatedRequest("POST", `/swaps`, {
            ...params,
            use_frontend_swap: true,
            use_gasless: params.use_gasless === true,
        }, { 'X-LS-CORRELATION-ID': correlationId });
        return this.resolveSwapDepositActions(response, response.data?.swap?.source_address ?? params.source_address);
    }
    async GetTransactionStatus(network, tx_id) {
        // 404 means the tx is not indexed yet. The rejection drives the caller's SWR error-retry polling.
        return await this.UnauthenticatedRequest("GET", `/transaction_status?network=${network}&transaction_id=${tx_id}`, undefined, undefined, { expectedStatuses: [404] });
    }
    async SwapCatchup(swapId, tx_id) {
        return await this.AuthenticatedRequest("POST", `/swaps/${swapId}/deposit_speedup`, { transaction_id: tx_id });
    }
    async AuthorizeSwapAsync(swapId, signature, signerAddress) {
        return await this.AuthenticatedRequest("POST", `/swaps/${swapId}/authorize`, { signature, signer_address: signerAddress });
    }
    async GetGaslessAuthorizationAsync(swapId) {
        return await this.AuthenticatedRequest("GET", `/swaps/${swapId}/authorize`);
    }
    async GetDepositActionsAsync(swapId, sourceAddress) {
        const query = sourceAddress ? `?source_address=${encodeURIComponent(sourceAddress)}` : "";
        const response = await this.AuthenticatedRequest("GET", `/swaps/${swapId}/deposit_actions${query}`);
        if (response.error || !Array.isArray(response.data)) {
            return response;
        }
        const actions = await this.resolveDepositActions(swapId, response.data, sourceAddress);
        return { ...response, data: actions };
    }
    async GetNextActionAsync(swapId, sourceAddress) {
        const query = sourceAddress ? `?source_address=${encodeURIComponent(sourceAddress)}` : '';
        return this.AuthenticatedRequest('GET', `/swaps/${swapId}/next_action${query}`);
    }
    async GetSwapAsync(swapId, sourceAddress) {
        const query = sourceAddress ? `?source_address=${encodeURIComponent(sourceAddress)}` : "";
        const response = await this.AuthenticatedRequest("GET", `/swaps/${swapId}${query}`);
        return this.resolveSwapDepositActions(response, sourceAddress);
    }
    resolveDepositActions(swapId, actions, sourceAddress) {
        return resolveAtomicDepositActions(actions, async () => {
            const next = await this.GetNextActionAsync(swapId, sourceAddress);
            if (!next.data || next.error) {
                throw new Error(next.error?.message || 'Could not load the atomic swap action');
            }
            return next.data;
        });
    }
    async resolveSwapDepositActions(response, sourceAddress) {
        const swapData = response.data;
        if (response.error || !swapData?.swap?.id || !Array.isArray(swapData.deposit_actions)) {
            return response;
        }
        const { swap, deposit_actions: actions } = swapData;
        try {
            const resolved = await this.resolveDepositActions(swap.id, actions, sourceAddress ?? swap.source_address);
            return { ...response, data: { ...swapData, deposit_actions: resolved } };
        }
        catch {
            // Preserve an authoritative swap (especially a successful create).
            // A later action refresh must succeed before opening the wallet.
            return { ...response, data: { ...swapData, deposit_actions: undefined } };
        }
    }
    async AuthenticatedRequest(method, endpoint, data, header, signal) {
        const finishTelemetry = startApiOperation(method, endpoint);
        let uri = LayerSwapApiClient.apiBaseEndpoint + "/api/v2" + endpoint;
        return await this._authInterceptor(uri, { method: method, data: data, signal, headers: { 'Access-Control-Allow-Origin': '*', ...(header ? header : {}) } })
            .then(res => {
            finishTelemetry(res?.data?.error ? 'failed' : 'succeeded', { http_status: res?.status }, res?.data?.data);
            return res?.data;
        })
            .catch(async (reason) => {
            finishTelemetry(reason?.code === 'ERR_CANCELED' ? 'cancelled' : 'failed', { http_status: reason?.response?.status });
            if (signal?.aborted && reason?.code === 'ERR_CANCELED') {
                return Promise.reject(reason);
            }
            if (reason instanceof AuthRefreshFailedError) {
                return Promise.resolve(new EmptyApiResponse());
            }
            else {
                reportApiError(endpoint, reason);
                return Promise.reject(reason);
            }
        });
    }
    async UnauthenticatedRequest(method, endpoint, data, header, options) {
        let uri = LayerSwapApiClient.apiBaseEndpoint + "/api/v2" + endpoint;
        return await this._unauthInterceptor(uri, { method: method, data: data, headers: { 'Access-Control-Allow-Origin': '*', ...(header ? header : {}) } })
            .then(res => {
            return res?.data;
        })
            .catch(async (reason) => {
            if (reason instanceof AuthRefreshFailedError) {
                return Promise.resolve(new EmptyApiResponse());
            }
            else {
                reportApiError(endpoint, reason, options);
                return Promise.reject(reason);
            }
        });
    }
}
LayerSwapApiClient.apiBaseEndpoint = AppSettings.LayerswapApiUri;
LayerSwapApiClient.apiKey = AppSettings.LayerswapApiKeys[AppSettings.ApiVersion || 'mainnet'];
export default LayerSwapApiClient;
function reportApiError(endpoint, reason, options) {
    if (options?.expectedStatuses?.includes(reason?.response?.status))
        return;
    const errorCode = reason?.response?.data?.error?.code;
    if (IGNORED_API_ERROR_CODES.includes(errorCode))
        return;
    let error;
    if (reason instanceof Error) {
        error = reason;
    }
    else {
        error = new Error(String(reason));
        error.name = "APIError";
    }
    ErrorHandler({
        type: 'APIError',
        endpoint: endpoint,
        status: reason?.response?.status,
        statusText: reason?.response?.statusText,
        responseData: reason?.response?.data,
        requestUrl: reason?.request?.url,
        requestMethod: reason?.request?.method,
        message: error.message,
        name: error.name,
        stack: error.stack,
        // Internal classification and recovery retain the original. The shared
        // reporting boundary owns identity and conversion to a public summary.
        cause: reason,
    });
}
export var DepositAddressSource;
(function (DepositAddressSource) {
    DepositAddressSource[DepositAddressSource["UserGenerated"] = 0] = "UserGenerated";
    DepositAddressSource[DepositAddressSource["Managed"] = 1] = "Managed";
})(DepositAddressSource || (DepositAddressSource = {}));
export var TransactionType;
(function (TransactionType) {
    TransactionType["Input"] = "input";
    TransactionType["Output"] = "output";
    TransactionType["Refuel"] = "refuel";
    TransactionType["Refund"] = "refund";
})(TransactionType || (TransactionType = {}));
export var BackendTransactionStatus;
(function (BackendTransactionStatus) {
    BackendTransactionStatus["Completed"] = "completed";
    BackendTransactionStatus["Failed"] = "failed";
    BackendTransactionStatus["Initiated"] = "initiated";
    BackendTransactionStatus["Pending"] = "pending";
})(BackendTransactionStatus || (BackendTransactionStatus = {}));
export var TransactionStatus;
(function (TransactionStatus) {
    TransactionStatus["Completed"] = "completed";
    TransactionStatus["Failed"] = "failed";
    TransactionStatus["Pending"] = "pending";
})(TransactionStatus || (TransactionStatus = {}));
export var DepositType;
(function (DepositType) {
    DepositType["Manual"] = "manual";
    DepositType["Wallet"] = "wallet";
})(DepositType || (DepositType = {}));
export var SwapType;
(function (SwapType) {
    SwapType["OnRamp"] = "cex_to_network";
    SwapType["OffRamp"] = "network_to_cex";
    SwapType["CrossChain"] = "network_to_network";
})(SwapType || (SwapType = {}));
export var WithdrawType;
(function (WithdrawType) {
    WithdrawType["Wallet"] = "wallet";
    WithdrawType["Manually"] = "manually";
    WithdrawType["Stripe"] = "stripe";
    WithdrawType["Coinbase"] = "coinbase";
    WithdrawType["External"] = "external";
})(WithdrawType || (WithdrawType = {}));
